import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  memo,
  useMemo,
} from "react";
import { FixedSizeList as List, ListChildComponentProps } from "react-window";
import { v4 as uuidv4 } from "uuid";
import useUrlParams from "../../../../hooks/useUrlParams";
import { getEnvVar } from "../../../../../../envUtils";
import { get_formated_time } from "../../../../utils";
import useTrueTwin from "../../../../hooks/useTrueTwin";
import "./Console.scss";
import { ArrowUp } from "./ConsoleSvg";
import CustomToolTip from "../../../../../CommonComponents/CustomToolTip";
import ShimmerMenuList from "../../../../../CommonComponents/ShimmerEffectDesign/ShimmerMenu";
import { base64ToHex } from "../../../../../../utils/commonFunctions";
import { fetchRawEntries } from "./Utils";
import { safeScrollToIndex } from "./helpers/scrollHelpers";
import type { RawConsoleEntry } from "./types/consoleTypes";
import { CONSOLE_ROW_HEIGHT } from "./constants/consoleConstants";

interface TableCellProps {
  width: number;
  content: string;
  children?: React.ReactNode;
}

/** Single cell with tooltip for the raw bytes table */
const TableCell = memo<TableCellProps>(({ width, content, children }) => (
  <div
    className="d-flex align-items-center justify-content-start"
    style={{ width: `${width}px` }}
  >
    <CustomToolTip title={content} placement="top">
      <span className="truncate__text">{children ?? content}</span>
    </CustomToolTip>
  </div>
));

interface RawRowItemData {
  entries: RawConsoleEntry[];
  selectedRow: RawConsoleEntry | null;
  show_selected_output_details: (entry: RawConsoleEntry) => void;
  base64ToHex: (str: string) => string;
  isOutputModalVisible: boolean;
  listRef: React.RefObject<unknown>;
  height: number;
}

/** Single row in the virtualized raw bytes list */
const Row = memo<ListChildComponentProps<RawRowItemData>>(
  ({ index, style, data }) => {
    const {
      entries,
      selectedRow,
      show_selected_output_details,
      base64ToHex: toHex,
      isOutputModalVisible,
      listRef,
      height,
    } = data;
    const entry = entries[index];
    const isSelected =
      entry?.CommandUid === selectedRow?.CommandUid && isOutputModalVisible;
    const prevEntryRef = useRef<RawConsoleEntry | null>(null);

    const timeStr =
      entry?.CommandDetails?.groundTimestamp != null
        ? get_formated_time(entry.CommandDetails.groundTimestamp / 1000)
        : "";
    const hexStr = toHex(
      entry?.CommandDetails?.completeFrame ?? ""
    );

    // Handles row click: opens output modal first, then scrolls row into the shortened viewing window
    const handleRowClick = useCallback(() => {
      if (!entry) return;
      show_selected_output_details(entry);
      if (prevEntryRef.current !== entry) {
        const rowTop = index * CONSOLE_ROW_HEIGHT;
        if (rowTop > height * 0.5 - 150) {
          const idx = entries?.findIndex(
            (e) => e?.CommandUid === entry?.CommandUid
          );
          if (idx != null && idx >= 0) {
            // Double rAF ensures React has re-rendered and browser has applied the new CSS layout
            requestAnimationFrame(() => {
              requestAnimationFrame(() => {
                safeScrollToIndex(
                  listRef as React.RefObject<{ scrollTo?: (n: number) => void; _outerRef?: HTMLElement; _listRef?: HTMLElement; outerRef?: HTMLElement; props?: { itemCount?: number; height?: number }; state?: { scrollOffset: number } }>,
                  idx,
                  CONSOLE_ROW_HEIGHT
                );
              });
            });
          }
        }
      }
      prevEntryRef.current = entry ?? null;
    }, [entry, entries, height, index, listRef, show_selected_output_details]);

    return (
      <div style={style}>
        <div className="table__body__row d-flex">
          <button
            type="button"
            className={`table__body__row__item gap-3 ${isSelected ? "selected-response" : ""}`}
            onClick={handleRowClick}
            style={{ justifyContent: "flex-start" }}
          >
            <TableCell width={160} content={timeStr} />
            <TableCell width={850} content={hexStr} />
          </button>
        </div>
      </div>
    );
  }
);

TableCell.displayName = "RawTableCell";
Row.displayName = "RawOutputRow";

export interface RawOutputListProps {
  height: number;
  show_selected_output_details: (entry: RawConsoleEntry) => void;
  selectedRow: RawConsoleEntry | null;
  isOutputModalVisible: boolean;
  entries: RawConsoleEntry[];
  setEntries: React.Dispatch<React.SetStateAction<RawConsoleEntry[]>>;
}

/** Virtualized list for raw bytes entries with WebSocket updates and pagination */
const RawOutputList = memo<RawOutputListProps>(function RawOutputList({
  height,
  show_selected_output_details,
  selectedRow,
  isOutputModalVisible,
  entries,
  setEntries,
}) {
  const { sat_id, scenario_id } = useUrlParams();
  const { truetwin_id } = useTrueTwin();
  const listRef = useRef<List>(null);
  const loadingRef = useRef(false);
  const cursorRef = useRef("");
  const isAtTopRef = useRef(true);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [isDataFound, setIsDataFound] = useState(false);
  const isWsLoadingRef = useRef(false);
  const isWsPendingRef = useRef(false);

  // Fetches the latest raw entries from the API, replacing the current list
  const loadLatestEntries = useCallback(
    async (fromWs: boolean) => {
      if (loadingRef.current) return;
      if (fromWs && isWsLoadingRef.current) {
        isWsPendingRef.current = true;
        return;
      }
      loadingRef.current = true;
      if (fromWs) {
        isWsLoadingRef.current = true;
      }
      try {
        const { data, cursor } = await fetchRawEntries(
          sat_id ?? "",
          truetwin_id ?? "",
          scenario_id ?? "",
          ""
        );
        setEntries(data as RawConsoleEntry[]);
        setIsDataFound(true);
        cursorRef.current = cursor;
        sessionStorage.setItem("globalCursor", cursor);
        if (isAtTopRef.current) {
          listRef.current?.scrollToItem(0, "start");
        }
      } finally {
        loadingRef.current = false;
        if (fromWs) {
          isWsLoadingRef.current = false;
          if (isWsPendingRef.current) {
            isWsPendingRef.current = false;
            loadLatestEntries(true);
          }
        }
        setIsDataFound(true);
      }
    },
    [sat_id, truetwin_id, scenario_id, setEntries]
  );

  // Appends older raw entries using cursor-based pagination
  const loadMoreEntries = useCallback(async () => {
    if (loadingRef.current || !cursorRef.current) return;
    loadingRef.current = true;
    try {
      const { data, cursor } = await fetchRawEntries(
        sat_id ?? "",
        truetwin_id ?? "",
        scenario_id ?? "",
        cursorRef.current
      );
      setEntries((prev) => [...prev, ...(data as RawConsoleEntry[])]);
      cursorRef.current = cursor;
      sessionStorage.setItem("globalCursor", cursor);
    } finally {
      loadingRef.current = false;
    }
  }, [sat_id, truetwin_id, scenario_id, setEntries]);

  // Blocks wheel and touch scroll on the list when a row is selected
  useEffect(() => {
    const outerEl = (listRef.current as any)?._outerRef as HTMLElement | null;
    if (!outerEl) return;
    if (isOutputModalVisible) {
      const blockScroll = (e: Event) => e.preventDefault();
      outerEl.addEventListener("wheel", blockScroll, { passive: false });
      outerEl.addEventListener("touchmove", blockScroll, { passive: false });
      outerEl.style.overflowY = "hidden";
      return () => {
        outerEl.removeEventListener("wheel", blockScroll);
        outerEl.removeEventListener("touchmove", blockScroll);
        outerEl.style.overflowY = "auto";
      };
    }
  }, [isOutputModalVisible]);

  // Triggers pagination at scroll boundaries
  const handleScroll = useCallback(
    ({
      scrollOffset,
      scrollDirection,
    }: {
      scrollOffset: number;
      scrollDirection: "forward" | "backward";
    }) => {
      if (isOutputModalVisible) return;
      setShowScrollTop(scrollOffset > 10);
      const list = listRef.current;
      if (!list) return;
      const totalHeight = (entries?.length ?? 0) * CONSOLE_ROW_HEIGHT;
      const bottomThreshold = 5;
      if (
        scrollDirection === "forward" &&
        scrollOffset + Number(list.props?.height ?? 0) >=
          totalHeight - bottomThreshold
      ) {
        loadMoreEntries();
      } else if (scrollDirection === "backward" && scrollOffset === 0) {
        loadLatestEntries(false);
      }
      isAtTopRef.current = scrollOffset === 0;
    },
    [entries?.length, isOutputModalVisible, loadMoreEntries, loadLatestEntries]
  );

  useEffect(() => {
    const token = sessionStorage.getItem("authentication");
    if (!truetwin_id || !token) return;
    const sessionId = uuidv4();
    const ws = new WebSocket(
      `${getEnvVar("REACT_APP_WEBSOCKET_BACKEND_BASE_URL")}groundstation/websocketstatus?truetwinId=${truetwin_id}&sessionId=${sessionId}`
    );
    ws.onopen = () =>
      ws.send(JSON.stringify({ type: "authorization", token }));
    ws.onmessage = () => {
      if (isAtTopRef.current) loadLatestEntries(true);
    };
    ws.onerror = console.error;
    return () => ws.close();
  }, [truetwin_id, loadLatestEntries]);

  // Listen for upload completion event to refresh list with new data
  useEffect(() => {
    const handleLoadCurrentData = () => {
      loadLatestEntries(false);
    };
    window.addEventListener("loadCurrentData", handleLoadCurrentData);
    return () => window.removeEventListener("loadCurrentData", handleLoadCurrentData);
  }, [loadLatestEntries]);

  useEffect(() => {
    loadLatestEntries(false);
  }, []);

  // Scrolls to the first item in the list
  const handleScrollToTop = useCallback(() => {
    listRef.current?.scrollToItem(0, "start");
  }, []);

  const itemData: RawRowItemData = useMemo(
    () => ({
      entries,
      selectedRow,
      show_selected_output_details,
      base64ToHex,
      isOutputModalVisible,
      listRef,
      height,
    }),
    [
      entries,
      selectedRow,
      show_selected_output_details,
      isOutputModalVisible,
      height,
    ]
  );

  return (
    <>
      {!isDataFound && <ShimmerMenuList />}
      <div
        className={`output__div__message__container ${(entries?.length === 0 || !entries) && isDataFound ? "d-flex" : "d-none"}`}
      >
        No data found.
      </div>
      <List
        ref={listRef}
        height={height}
        itemCount={entries?.length ?? 0}
        itemSize={CONSOLE_ROW_HEIGHT}
        width="100%"
        onScroll={handleScroll}
        overscanCount={5}
        itemData={itemData}
        className="row_change_position_console_table modal__scrollbar__white__vertical"
        style={{ overflowX: "hidden", overflowY: isOutputModalVisible ? "hidden" : "auto" }}
      >
        {Row}
      </List>
      <button
        type="button"
        onClick={handleScrollToTop}
        title="Scroll to top"
        className={`scroll-to-top raw_byte_modal ${showScrollTop && !isOutputModalVisible ? "" : "hidden"} ${isOutputModalVisible ? "" : "scroll-to-top-middle"}`}
      >
        <ArrowUp />
      </button>
    </>
  );
});

export default RawOutputList;
